import { customElement, scss } from '../../core/ids-decorators';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { attributes } from '../../core/ids-attributes';
import {
  applyTextColorValue, IdsColorValue, IdsColorValueCategories, isValidColor
} from '../../utils/ids-color-utils/ids-color-utils';

import '../ids-icon/ids-icon';
import '../ids-layout-flex/ids-layout-flex';
import type IdsIcon from '../ids-icon/ids-icon';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';

import styles from './ids-kpi.scss';
import { isValidInteger } from '../../utils/ids-number-utils/ids-number-utils';

enum Trend {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
}

enum TrendTextColor {
  POSITIVE = 'success',
  NEGATIVE = 'error',
  NEUTRAL = 'neutral',
}

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS kpi Component
 * @type {IdsKpi}
 * @inherits IdsElement
 * @part kpi - the kpi container element
 */
@customElement('ids-kpi')
@scss(styles)
export default class IdsKpi extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
  }

  /**
   * Invoked when one of the custom element's attributes is changed.
   * @param {string} name The attribute name
   * @param {string} oldValue The old value
   * @param {string} newValue The new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === attributes.TREND_LABEL && oldValue !== newValue) {
      const elem = this.container?.querySelector('.trend-label');
      if (elem) {
        elem.removeAttribute('hidden');
      }
      this.#applyTrendFormat();
    }
  }

  /**
   * Setup the Event Handling
   * @private
   */
  #attachEventHandlers(): void {
    this.onLanguageChange = () => {
      this.#applyKpiFormat();
      this.#applyTrendFormat();
    };
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.COMPACT,
      attributes.COL_SPAN,
      attributes.ICON,
      attributes.KPI,
      attributes.KPI_FORMAT,
      attributes.MAIN_LABEL,
      attributes.TREND_LABEL,
      attributes.TREND_LABEL_ICON_COLOR,
      attributes.TREND_LABEL_NO_ICON,
      attributes.TREND_LABEL_COLOR,
      attributes.KPI_FORMAT,
      attributes.SELECTED,
      attributes.SHOW_ALL,
      attributes.STATUS_COLOR,
      attributes.SUBTITLE
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const isNegative = (this.trendLabel || '').indexOf('-') > -1;
    const isPositive = !isNegative && this.trendLabel !== '' && this.trendLabel !== '.';
    const isCompact = this.compact;

    // Adjust font sizes based on compact mode
    const kpiFontSize = isCompact ? 28 : 40;
    const kpiLabelSize = 12;
    const kpiSubtitleSize = 10;

    const markup = `<div class="ids-kpi${this.showAll ? ' show-all' : ''}${isCompact ? ' compact' : ''}" part="kpi">
        <ids-layout-flex direction="column">
          <ids-layout-flex justify-content="space-between" align-items="center">
            <ids-layout-flex-item class="trend-container${this.showAll ? ' show-all' : ''}">
              <div class="trend-label${isPositive ? ' is-positive' : ''}${isNegative ? ' is-negative' : ''}${this.showAll ? ' show-always' : ''}"></div>
              ${this.icon ? `<div class="main-icon${this.showAll ? ' show-always' : ''}"><ids-icon icon="${this.icon}" status-color="${this.statusColor}"></ids-icon></div>` : ''}
            </ids-layout-flex-item>
          </ids-layout-flex>
          <ids-layout-flex-item>
            <ids-text font-size="${kpiFontSize}" class="kpi-label" font-weight="semi-bold" overflow="ellipsis" tooltip="true">${this.kpi}</ids-text>
          </ids-layout-flex-item>
          <ids-layout-flex-item>
            <ids-text font-size="${kpiLabelSize}" class="main-label" font-weight="semi-bold" overflow="ellipsis"  tooltip="true">${this.mainLabel}</ids-text>
          </ids-layout-flex-item>
          <ids-layout-flex-item>
            <ids-text font-size="${kpiSubtitleSize}" class="subtitle" overflow="ellipsis" tooltip="true">${this.subtitle}</ids-text>
          </ids-layout-flex-item>
        </ids-layout-flex>
      </div>`;
    return markup;
  }

  /**
   * Set the compact attribute
   * @param {string | boolean} value true or false. Component will
   * default to regular size if this property is omitted.
   */
  set compact(value: string | boolean) {
    const isCompact = stringToBool(value);
    this.setAttribute(attributes.COMPACT, isCompact ? 'true' : 'false');
    const kpiLabel = this.container?.querySelector('.kpi-label');
    if (kpiLabel) kpiLabel.setAttribute('font-size', isCompact ? '28' : '40');

    // Update the container class
    if (this.container) {
      if (isCompact) {
        this.container.classList.add('compact');
      } else {
        this.container.classList.remove('compact');
      }
    }
    this.#applyKpiFormat();
    this.#applyTrendFormat();
  }

  get compact(): boolean {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  /**
   * Returns the trending up icon SVG with the specified color
   * @param {string} color The fill color for the icon
   * @returns {string} The SVG markup
   */
  trendingUpIcon(color: string = 'var(--ids-color-success-default)'): string {
    return `<svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.4 12L0 10.6L7.4 3.15L11.4 7.15L16.6 2H14V0H20V6H18V3.4L11.4 10L7.4 6L1.4 12Z" fill="${color}"/>
    </svg>`;
  }

  /**
   * Returns the trending down icon SVG with the specified color
   * @param {string} color The fill color for the icon
   * @returns {string} The SVG markup
   */
  trendingDownIcon(color: string = 'var(--ids-color-error-default)'): string {
    return `<svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.4 0L0 1.4L7.4 8.85L11.4 4.85L16.6 10H14V12H20V6H18V8.6L11.4 2L7.4 6L1.4 0Z" fill="${color}"/>
    </svg>`;
  }

  /**
   * Set the trend of the KPI component (Optional).
   * @param {string} value Trend text
   */
  set trend(value: string) {
    const val = value || '';

    // return if value is not within Trend enum values
    if (val && !Object.values(Trend).includes(val as Trend)) {
      return;
    }

    if (val.length > 0) {
      this.setAttribute(attributes.TREND, value);
      this.#applyTrendFormat();
    } else {
      this.removeAttribute(attributes.TREND);
    }
  }

  get trend(): Trend | '' {
    return (this.getAttribute(attributes.TREND) as Trend) || '';
  }

  /**
   * Used to showcase price or amount trending up or down (Optional).
   * @param {string} value Trend label text
   */
  set trendLabel(value: string) {
    const val = value || '';
    const elem = this.container?.querySelector('.trend-label');

    if (Number(val) === 0) {
      this.setAttribute(attributes.TREND_LABEL, val);
      this.#applyTrendFormat();
    } else if (val.length > 0) {
      this.setAttribute(attributes.TREND_LABEL, value);
      elem?.removeAttribute('hidden');
      this.#applyTrendFormat();
    } else {
      this.removeAttribute(attributes.TREND_LABEL);
      elem?.setAttribute('hidden', 'true');
    }
  }

  get trendLabel(): string {
    return this.getAttribute(attributes.TREND_LABEL) || '';
  }

  /**
   * Set the trend label color
   * @param {IdsColorValue | IdsColorValueCategories | string} value The color value
   */
  set trendLabelColor(value: IdsColorValue | IdsColorValueCategories | string) {
    if (!value) {
      this.removeAttribute(attributes.TREND_LABEL_COLOR);
      return;
    }

    if (typeof value === 'string' && value !== '') {
      if (isValidColor(value)) {
        this.setAttribute(attributes.TREND_LABEL_COLOR, value);
        applyTextColorValue(value as IdsColorValue, this.container as HTMLElement);
        this.#applyTrendFormat();
      } else {
        this.removeAttribute(attributes.TREND_LABEL_COLOR);
      }
    }
  }

  get trendLabelColor(): IdsColorValue | IdsColorValueCategories | string {
    return this.getAttribute(attributes.TREND_LABEL_COLOR) as IdsColorValue;
  }

  /**
   * Set the trend label icon color
   * @param {IdsColorValue | IdsColorValueCategories | string} value The color value
   */
  set trendLabelIconColor(value: IdsColorValue | IdsColorValueCategories | string) {
    if (!value) {
      this.removeAttribute(attributes.TREND_LABEL_ICON_COLOR);
      return;
    }

    if (typeof value === 'string' && value !== '') {
      if (isValidColor(value)) {
        this.setAttribute(attributes.TREND_LABEL_ICON_COLOR, value);
        this.#applyTrendFormat();
      } else {
        this.removeAttribute(attributes.TREND_LABEL_ICON_COLOR);
      }
    }
  }

  get trendLabelIconColor(): IdsColorValue | IdsColorValueCategories | string {
    return this.getAttribute(attributes.TREND_LABEL_ICON_COLOR) as IdsColorValue;
  }

  /**
   * Set whether to hide the trend label icon
   * @param {boolean} value True to hide the icon, false to show it
   */
  set trendLabelNoIcon(value: boolean) {
    // Skip setting when set to non-boolean value
    if (!isValidBooleanValue(value)) {
      return;
    }

    const noIcon = stringToBool(value);
    this.toggleAttribute(attributes.TREND_LABEL_NO_ICON, noIcon);

    this.#applyTrendFormat();
  }

  get trendLabelNoIcon() {
    return stringToBool(this.getAttribute(attributes.TREND_LABEL_NO_ICON)) || false;
  }

  /**
   * Use to call attention to status of the KPI.
   * Can be colored to bring attention (via statusColor)
   * (Optional in larger KPI format).
   * @param {string} value Trend label text
   */
  set icon(value: string | null) {
    if (value) {
      this.setAttribute(attributes.ICON, value);

      let iconContainer = this.container?.querySelector('.main-icon');
      let iconElem = this.container?.querySelector<IdsIcon>('.main-icon ids-icon');

      if (!iconContainer) {
        const trendContainer = this.container?.querySelector('.trend-container');
        if (trendContainer) {
          iconContainer = document.createElement('div');
          iconContainer.className = 'main-icon';
          trendContainer.appendChild(iconContainer);
        }
      }

      if (!iconElem && iconContainer) {
        iconElem = document.createElement('ids-icon') as IdsIcon;
        iconContainer.appendChild(iconElem);
      }

      if (iconElem) {
        iconElem.icon = value;
        iconElem.setAttribute('status-color', this.statusColor);
        iconElem.removeAttribute('hidden');
      }
    } else {
      this.removeAttribute(attributes.ICON);
      const iconElem = this.container?.querySelector<IdsIcon>('.main-icon ids-icon');
      if (iconElem) {
        iconElem.setAttribute('hidden', 'true');
      }
    }
  }

  get icon(): string | null {
    return this.getAttribute(attributes.ICON);
  }

  /**
   * Color that can be used for embellishment or to indicate status or bring attention
   * @param {string} value Any pallete color reference
   */
  set statusColor(value: string | null) {
    if (value) {
      this.setAttribute(attributes.STATUS_COLOR, value);

      const iconElem = this.container?.querySelector('.main-icon ids-icon');
      if (iconElem) {
        iconElem.setAttribute('status-color', value);
      }
    } else {
      this.removeAttribute(attributes.STATUS_COLOR);
    }
  }

  get statusColor(): string {
    return this.getAttribute(attributes.STATUS_COLOR) || 'blue';
  }

  /**
   * Large center label. Show up to 6 characters in small size in the KPI area
   * @param {string} value Numbers or percentages or dollar amount
   */
  set kpi(value: string | null) {
    if (value) {
      this.setAttribute(attributes.KPI, value);
    } else {
      this.removeAttribute(attributes.KPI);
    }
    this.#applyKpiFormat();
  }

  get kpi(): string {
    return this.getAttribute(attributes.KPI) || '';
  }

  /**
   * Main KPI Label. Will truncate if too big
   * @param {string} value label value as text
   */
  set mainLabel(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAIN_LABEL, value);

      const elem = this.container?.querySelector('.main-label');
      if (elem) {
        elem.textContent = value;
      }
    } else {
      this.removeAttribute(attributes.MAIN_LABEL);

      const elem = this.container?.querySelector('.main-label');
      if (elem) {
        elem.textContent = '';
      }
    }
  }

  get mainLabel(): string {
    return this.getAttribute(attributes.MAIN_LABEL) || '';
  }

  /**
   * Extra details on the KPI. Single line. Auto truncates.
   * @param {string} value label value as text
   */
  set subtitle(value: string | null) {
    if (value) {
      this.setAttribute(attributes.SUBTITLE, value);

      const elem = this.container?.querySelector('.subtitle');
      if (elem) {
        elem.textContent = value;
      }
    } else {
      this.removeAttribute(attributes.SUBTITLE);

      const elem = this.container?.querySelector('.subtitle');
      if (elem) {
        elem.textContent = '';
      }
    }
  }

  get subtitle(): string {
    return this.getAttribute(attributes.SUBTITLE) || '';
  }

  /**
   * Set the col-span attribute
   * @param {string | null} value If 2 will span 2 columns, nothing else is currently valid
   */
  set colSpan(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COL_SPAN, value);
      this.container?.classList.add('colspan');
    } else {
      this.removeAttribute(attributes.COL_SPAN);
      this.container?.classList.remove('colspan');
    }
  }

  /**
   * Get col-span attribute
   * @returns {string | null} The number value for the columns to span in the grid
   */
  get colSpan(): string | null {
    return this.getAttribute(attributes.COL_SPAN);
  }

  formats: {
    kpi?: Intl.NumberFormatOptions | undefined | string,
    trend?: Intl.NumberFormatOptions | undefined | string
  } = {
      kpi: undefined,
      trend: { signDisplay: 'exceptZero' }
    };

  /**
   * Set the locale format for the kpi
   * @param {Intl.NumberFormatOptions | undefined | string} value If 2 will span 2 columns, nothing else is valid
   */
  set kpiFormat(value: Intl.NumberFormatOptions | undefined | string) {
    if (value !== undefined && typeof value !== 'string') {
      this.formats.kpi = value;
    } else {
      this.formats.kpi = typeof value === 'string' ? '' : undefined;
    }
    this.#applyKpiFormat();
  }

  /**
   * Get col-span attribute
   * @returns {Intl.NumberFormatOptions | undefined | string} The number value for the columns to span in the grid
   */
  get kpiFormat(): Intl.NumberFormatOptions | undefined | string {
    return this.formats.kpi;
  }

  /**
   * Set the dropdown to combobox feature
   * @param {string} value  The value of combobox
   */
  set showAll(value: string) {
    this.setAttribute(attributes.SHOW_ALL, value);
  }

  get showAll(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_ALL));
  }

  /**
   * Apply the format on the field
   */
  #applyKpiFormat() {
    const elem = this.container?.querySelector('.kpi-label');
    if (elem && typeof this.kpiFormat === 'string') {
      elem.textContent = this.kpi;
      return;
    }
    if (elem && typeof this.kpiFormat !== 'string') {
      const kpiValue = Number(this.kpi);
      if (!Number.isNaN(kpiValue)) {
        elem.textContent = new Intl.NumberFormat(this.locale || 'en', this.kpiFormat).format(kpiValue);
      } else {
        elem.textContent = this.kpi;
      }
    }
  }

  /**
   * Set the locale format for the kpi
   * @param {Intl.NumberFormatOptions | undefined} value If 2 will span 2 columns, nothing else is valid
   */
  set trendFormat(value: Intl.NumberFormatOptions | undefined | string) {
    if (value !== undefined && typeof value !== 'string') {
      this.formats.trend = value;
    } else {
      this.formats.trend = typeof value === 'string' ? '' : undefined;
    }
    this.#applyTrendFormat();
  }

  /**
   * Get col-span attribute
   * @returns {Intl.NumberFormatOptions | undefined | string} The number value for the columns to span in the grid
   */
  get trendFormat(): Intl.NumberFormatOptions | undefined | string {
    return this.formats.trend;
  }

  /**
   * Set the trend label and icon color for negative trends
   * @private
   */
  #applyNegativeTrendStyling() {
    const trendIcon = this.shadowRoot?.querySelector('svg');
    const trendLabel = this.container?.querySelector('.trend-label') as HTMLElement;

    // Always apply error color to the trend label text for negative values
    applyTextColorValue(this.trendLabelColor ?? TrendTextColor.NEGATIVE, trendLabel);

    if (trendIcon) {
      const iconColor = this.trendLabelIconColor ? `var(--ids-color-${this.trendLabelIconColor})` : trendLabel?.style.color || 'var(--ids-color-error-default)';
      trendIcon.outerHTML = this.trendingDownIcon(iconColor);
    }
  }

  /**
   * Apply formatting and styling to the trend label element
   * Handles both text and numeric trend values with appropriate colors and icons
   * @private
   */
  #applyTrendFormat() {
    const elem = this.container?.querySelector('.trend-label');
    if (elem && typeof this.trendFormat === 'string') {
      elem.textContent = this.trendLabel;
      return;
    }
    if (typeof this.trendFormat !== 'string' && elem) {
      const isIntegerValue = isValidInteger(this.trendLabel);

      if (!isIntegerValue) {
        this.#applyTextTrendLabel(elem);
        return;
      }

      this.#applyIntegerTrendLabel(elem);
    }
  }

  /**
   * Apply trend formatting for text-based trend labels
   * Uses trend attribute to determine color and icon direction
   * @param {Element} elem The trend label element to format
   * @private
   */
  #applyTextTrendLabel(elem: Element) {
    if (!elem) return;

    // If trend has no value, retain original behavior
    if (!this.trend) {
      const textColor = this.trendLabelColor || TrendTextColor.NEUTRAL;
      applyTextColorValue(textColor, elem as HTMLElement);
      elem.classList.remove('is-positive', 'is-negative');
      elem.innerHTML = this.trendLabel;
    } else {
      this.#displayTrend(elem, this.trendLabel, this.trend);
    }
  }

  /**
   * Apply trend formatting for numeric trend labels with locale formatting
   * Determines trend direction from numeric value or trend attribute override
   * @param {Element} elem The trend label element to format
   * @private
   */
  #applyIntegerTrendLabel(elem: Element) {
    if (typeof this.trendFormat === 'string' || this.trendLabel === '' || !elem) {
      return;
    }

    let formatted = new Intl.NumberFormat(this.locale || 'en', this.trendFormat).format(Number(this.trendLabel));
    const isNegative = (formatted || '').indexOf('-') > -1;
    if (formatted === '٠') formatted = '';
    const isPositive = !isNegative && formatted !== '';

    // If trend has value, use it to override direction; otherwise use original behavior
    let finalIsPositive = isPositive;
    let finalIsNegative = isNegative;

    if (Number(this.trendLabel) === 0) {
      finalIsPositive = false;
      finalIsNegative = false;
      elem.classList.add('is-positive');
    }

    if (this.trend) {
      finalIsPositive = this.trend === Trend.POSITIVE;
      finalIsNegative = this.trend === Trend.NEGATIVE;
    }

    let finalTrend = Trend.NEUTRAL;

    finalTrend = finalIsPositive ? Trend.POSITIVE : finalTrend;
    finalTrend = finalIsNegative ? Trend.NEGATIVE : finalTrend;

    this.#displayTrend(elem, formatted, finalTrend);

    if (finalTrend === Trend.NEGATIVE) {
      this.#applyNegativeTrendStyling();
    }

    if (Number(this.trendLabel) === 0) {
      elem.classList.add('is-positive');
    }
  }

  /**
   * Display trend with appropriate styling, colors, and icons
   * @param {Element} elem The element to apply styling to
   * @param {string} label The text content to display
   * @param {Trend} trend The trend direction (positive, negative, or neutral)
   * @private
   */
  #displayTrend(elem: Element, label: string, trend: Trend) {
    const isPositive = trend === Trend.POSITIVE;
    const isNegative = trend === Trend.NEGATIVE;

    // Set icon based on trend direction
    const trendIconColor = (color: string) => {
      const tempElement = document.createElement('div');
      applyTextColorValue(color, tempElement);

      if (this.trendLabelIconColor) return `var(--ids-color-${this.trendLabelIconColor})`;

      return tempElement.style.color || `var(--ids-color-${color}-default)`;
    };

    let trendIcon = '';
    let textColor = this.trendLabelColor;
    if (isPositive) {
      textColor = textColor || TrendTextColor.POSITIVE;
      trendIcon = this.trendingUpIcon(trendIconColor(textColor));
    } else if (isNegative) {
      textColor = textColor || TrendTextColor.NEGATIVE;
      trendIcon = this.trendingDownIcon(trendIconColor(textColor));
    } else {
      textColor = textColor || TrendTextColor.NEUTRAL;
    }

    applyTextColorValue(textColor, elem as HTMLElement);

    elem.classList.toggle('is-positive', isPositive);
    elem.classList.toggle('is-negative', isNegative);
    elem.innerHTML = `${label}${this.trendLabelNoIcon || !trendIcon ? '' : trendIcon}`;
  }
}
